/**
 * Which anchor clicks the route progress bar should announce.
 *
 * A plain left click on a same-origin link whose path or query differs from
 * the page the visitor is on is a navigation the app will perform and the
 * visitor will wait for. Everything else is either not a navigation (a hash
 * jump, the link to the very page) or one the browser handles on its own (a
 * new tab through a modifier key or `target`, a download, `mailto:`, an
 * external site), and a bar for those would sit at 80 % until it stalled out.
 */

export type ClickLike = {
  button: number;
  metaKey: boolean;
  ctrlKey: boolean;
  shiftKey: boolean;
  altKey: boolean;
};

export type AnchorLike = {
  href: string;
  target: string;
  hasAttribute(name: string): boolean;
};

export type LocationLike = {
  origin: string;
  pathname: string;
  search: string;
};

export function navigationTargetFromClick(
  event: ClickLike,
  anchor: AnchorLike,
  current: LocationLike,
): URL | null {
  if (event.button !== 0) return null;
  if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey) return null;
  if (anchor.target && anchor.target !== "_self") return null;
  if (anchor.hasAttribute("download")) return null;
  if (!anchor.href) return null;

  let url: URL;
  try {
    url = new URL(anchor.href, current.origin);
  } catch {
    return null;
  }
  if (url.protocol !== "http:" && url.protocol !== "https:") return null;
  if (url.origin !== current.origin) return null;
  if (url.pathname === current.pathname && url.search === current.search) return null;
  return url;
}
